import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashBoardActions } from "../../../store/ducks/dashboard";
import { AppActions } from "../../../store/ducks/app";
import { AuthActions } from "../../../store/ducks/auth";
import { OperationalDashboardActions } from "../../../store/ducks/operationalDashboard";
import {
	CopilotPatientReference,
	fetchFullPatientForDashboard,
	isAbortError,
} from "../../OperationalDashboard/services/clinicalDashboardService";

export const useAICopilotBannerViewModel = () => {
	const dispatch = useDispatch();

	const totalPatients = useSelector(
		(state: any) => state.dashboard.total_pacientes
	);
	const reduxPatients = useSelector((state: any) => state.dashboard.pacientes);
	const loggedUser = useSelector((state: any) => state?.auth?.user);
	const latestEvaluation = useSelector(
		(state: any) => state.operationalDashboard.ultimaAvaliacaoFinalizada
	);
	const metrics = useSelector((state: any) => ({
		reavaliacao: state.operationalDashboard.prontosReavaliacao?.total || 0,
		inativos: state.operationalDashboard.pacientesInativos?.total || 0,
	}));
	const summaryLoading = useSelector(
		(state: any) => !!state.operationalDashboard.loadingResumo
	);
	const summaryLoaded = useSelector(
		(state: any) => !!state.operationalDashboard.hasLoadedResumo
	);
	const summaryError = useSelector(
		(state: any) => state.operationalDashboard.resumoError || null
	);
	const hasOperationalDashboardAccess = true;

	const [isV1Mode, setIsV1Mode] = useState<boolean>(false);
	const [selectedPatient, setSelectedPatient] =
		useState<CopilotPatientReference | null>(null);
	const [isPatientListModalOpen, setIsPatientListModalOpen] = useState(false);
	const [isPatientAreaLoading, setIsPatientAreaLoading] = useState(false);
	const [patientAreaError, setPatientAreaError] = useState<string | null>(null);
	const patientRequest = useRef<AbortController | null>(null);

	useEffect(() => {
		dispatch(OperationalDashboardActions.operational_resumo_request());
	}, [dispatch]);

	useEffect(
		() => () => {
			patientRequest.current?.abort();
		},
		[]
	);

	// Determines if the base is empty (no patients)
	const isEmptyState = useMemo(() => {
		return (
			(!reduxPatients || reduxPatients.length === 0) && totalPatients === 0
		);
	}, [reduxPatients, totalPatients]);

	const latestPatient = useMemo<CopilotPatientReference | null>(() => {
		if (!latestEvaluation?.paciente) return null;
		return {
			id: latestEvaluation.paciente.id,
			nome: latestEvaluation.paciente.nome,
			foto: latestEvaluation.paciente.foto || null,
			realizadaEm: latestEvaluation.realizadaEm || null,
		};
	}, [latestEvaluation]);

	const activePatient = selectedPatient || latestPatient;

	const handleStartFirstEvaluation = () => {
		dispatch(AppActions.set_modal_adicionar_paciente(true));
	};

	const handleOpenPatientListModal = () => {
		const hasNoPatients =
			(!reduxPatients || reduxPatients.length === 0) && totalPatients === 0;
		if (hasNoPatients) {
			handleStartFirstEvaluation();
			return;
		}
		setIsPatientListModalOpen(true);
	};

	const handleGoToPanel = () => {
		dispatch(AppActions.set_option_menu(false, "Painel Operacional"));
		dispatch(DashBoardActions.set_current_page("painel_operacional"));
	};

	const retrySummary = useCallback(
		() => dispatch(OperationalDashboardActions.operational_resumo_request()),
		[dispatch]
	);

	const handleGoToPatientArea = useCallback(async () => {
		if (!activePatient?.id || !loggedUser?.token || isPatientAreaLoading)
			return;
		patientRequest.current?.abort();
		const controller = new AbortController();
		patientRequest.current = controller;
		setIsPatientAreaLoading(true);
		setPatientAreaError(null);
		try {
			const patient = await fetchFullPatientForDashboard(
				loggedUser.token,
				activePatient.id,
				controller.signal
			);
			if (!patient) {
				throw new Error("Não foi possível carregar o cadastro do paciente.");
			}
			dispatch(AppActions.set_open_paciente(patient));
			dispatch(DashBoardActions.set_current_page("perfil_paciente"));
		} catch (error) {
			if (!isAbortError(error)) {
				if ((error as any)?.status === 401) dispatch(AuthActions.auth_logout());
				setPatientAreaError(
					(error as Error)?.message ||
						"Não foi possível abrir a área do paciente."
				);
			}
		} finally {
			if (!controller.signal.aborted) setIsPatientAreaLoading(false);
		}
	}, [activePatient, loggedUser?.token, isPatientAreaLoading, dispatch]);

	return {
		isV1Mode,
		hasOperationalDashboardAccess,
		setIsV1Mode,
		isEmptyState,
		activePatient,
		setSelectedPatient,
		metrics,
		summaryLoading: summaryLoading && !summaryLoaded,
		summaryError: !summaryLoaded ? summaryError : null,
		retrySummary,
		isPatientAreaLoading,
		patientAreaError,
		handleStartFirstEvaluation,
		isPatientListModalOpen,
		setIsPatientListModalOpen,
		handleOpenPatientListModal,
		handleGoToPanel,
		handleGoToPatientArea,
	};
};
